import { React, useState } from 'react';
import { Box, Collapse, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ContainerScroll } from '@goldblack/client-comps';

import {
    AdminInfrastructure,
    AdminSettings,
    AdminConfig,
    AccountFilters,
    AccountUsers,
    AccountMl,
    LoiseConfig,
    LoiseFilters,
    LoiseLegal,
    LoiseReports,
    LoiseUsers,
    parseDeepLink,
} from '../';
import { AccountDashboard } from '../dashboard/account/account-dashboard';
import { LoiseDashboard } from '../dashboard/loise/loise-dashboard';
import { LoiseMl } from '../management/loise/loise-ml';
import { InfraDashboard } from '../infrastructure/infra-dashboard';
import { SchedulerStats } from '../infrastructure/scheduler-stats';
import { CacheStats } from '../dashboard/loise/cache-stats';

const NAV_TREE = [
    {
        id: 'services',
        label: 'Services',
        children: [
            {
                id: 'services.account',
                label: 'GoldBlack',
                children: [
                    { id: 'services.account.dashboard', label: 'Dashboard' },
                    { id: 'services.account.configs', label: 'Configs' },
                    { id: 'services.account.filters', label: 'Filters' },
                    { id: 'services.account.scheduler', label: 'Scheduler' },
                    { id: 'services.account.users', label: 'Users' },
                    { id: 'services.account.ml', label: 'ML' },
                ],
            },
            {
                id: 'services.loise',
                label: 'Loise',
                children: [
                    { id: 'services.loise.dashboard', label: 'Dashboard' },
                    { id: 'services.loise.cache', label: 'Cache' },
                    { id: 'services.loise.configs', label: 'Configs' },
                    { id: 'services.loise.filters', label: 'Filters' },
                    { id: 'services.loise.legal', label: 'Legal' },
                    { id: 'services.loise.reports', label: 'Reports' },
                    { id: 'services.loise.scheduler', label: 'Scheduler' },
                    { id: 'services.loise.users', label: 'Users' },
                    { id: 'services.loise.ml', label: 'ML' },
                ],
            },
        ],
    },
    {
        id: 'infrastructure',
        label: 'Infrastructure',
        children: [
            { id: 'infrastructure.dashboard', label: 'Dashboard' },
            { id: 'infrastructure.aws', label: 'AWS' },
            { id: 'infrastructure.cloudflare', label: 'Cloudflare' },
            { id: 'infrastructure.google', label: 'Google' },
        ],
    },
    { id: 'settings', label: 'Settings' },
];

const SERVICE_NODE = {
    'loise/users': 'services.loise.users',
    'loise/reports': 'services.loise.reports',
    loise: 'services.loise.dashboard',
    account: 'services.account.dashboard',
    ml: 'infrastructure.aws',
};

function flatLabels(tree, acc = {}) {
    for (const node of tree) {
        acc[node.id] = node.label;
        if (node.children) flatLabels(node.children, acc);
    }
    return acc;
}
const LABELS = flatLabels(NAV_TREE);

function initialExpanded(nodeId) {
    const parts = nodeId.split('.');
    const expanded = new Set();
    for (let i = 1; i < parts.length; i++) {
        expanded.add(parts.slice(0, i).join('.'));
    }
    return expanded;
}

const DEFAULT_NODE = 'services.loise.dashboard';

function NavGroup({ node, activeNode, expanded, onToggle, onSelect }) {
    const isExpanded = expanded.has(node.id);
    const isLeaf = !node.children;
    const isActive = activeNode === node.id;

    if (isLeaf) {
        return (
            <ListItemButton selected={isActive} onClick={() => onSelect(node.id)} sx={{ pl: 2, py: 0.5 }}>
                <ListItemText primary={node.label} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
        );
    }

    return (
        <>
            <ListItemButton onClick={() => onToggle(node.id)} sx={{ pl: 2, py: 0.75 }}>
                <ListItemText primary={node.label} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
                {isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
            </ListItemButton>
            <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                <Box sx={{ ml: 2.5, borderLeft: '1px solid', borderColor: 'action.disabled' }}>
                    <List disablePadding>
                        {node.children.map((child) => (
                            <NavGroup
                                key={child.id}
                                node={child}
                                activeNode={activeNode}
                                expanded={expanded}
                                onToggle={onToggle}
                                onSelect={onSelect}
                            />
                        ))}
                    </List>
                </Box>
            </Collapse>
        </>
    );
}

function renderContent(nodeId, props) {
    switch (nodeId) {
        case 'services.account.dashboard':
            return <AccountDashboard />;
        case 'services.account.scheduler':
            return <SchedulerStats service='account' />;
        case 'services.account.configs':
            return <AdminConfig />;
        case 'services.account.filters':
            return <AccountFilters {...props} />;
        case 'services.account.users':
            return <AccountUsers {...props} />;
        case 'services.account.ml':
            return <AccountMl />;
        case 'services.loise.dashboard':
            return <LoiseDashboard />;
        case 'services.loise.cache':
            return <CacheStats />;
        case 'services.loise.scheduler':
            return <SchedulerStats service='loise' />;
        case 'services.loise.configs':
            return <LoiseConfig />;
        case 'services.loise.filters':
            return <LoiseFilters {...props} />;
        case 'services.loise.legal':
            return <LoiseLegal />;
        case 'services.loise.reports':
            return <LoiseReports {...props} />;
        case 'services.loise.users':
            return <LoiseUsers {...props} />;
        case 'services.loise.ml':
            return <LoiseMl />;
        case 'infrastructure.dashboard':
            return <InfraDashboard />;
        case 'infrastructure.aws':
            return <AdminInfrastructure cloud='aws' {...props} />;
        case 'infrastructure.cloudflare':
            return <AdminInfrastructure cloud='cloudflare' />;
        case 'infrastructure.google':
            return <AdminInfrastructure cloud='google' />;
        case 'settings':
            return <AdminSettings {...props} />;
        default:
            return null;
    }
}

function AdminBody(props) {
    const [activeNode, setActiveNode] = useState(DEFAULT_NODE);
    const [expanded, setExpanded] = useState(() => initialExpanded(DEFAULT_NODE));
    const [deeplink, setDeeplink] = useState(null);

    const toggle = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const select = (id) => {
        setActiveNode(id);
        setExpanded((prev) => {
            const next = new Set(prev);
            initialExpanded(id).forEach((k) => next.add(k));
            next.add(id);
            return next;
        });
    };

    const onDeepLink = (url) => {
        const dl = parseDeepLink(url);
        const node = SERVICE_NODE[`${dl.path[0]}/${dl.path[1]}`] ?? SERVICE_NODE[dl.path[0]];
        if (node) select(node);
        setDeeplink(dl);
    };

    const childProps = { ...props, deeplink, onDeepLink };

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
            {/* Left nav */}
            <Box
                sx={{
                    width: 220,
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                <ContainerScroll>
                    <List disablePadding>
                        {NAV_TREE.map((node) => (
                            <NavGroup
                                key={node.id}
                                node={node}
                                activeNode={activeNode}
                                expanded={expanded}
                                onToggle={toggle}
                                onSelect={select}
                            />
                        ))}
                    </List>
                </ContainerScroll>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant='h6'>{LABELS[activeNode]}</Typography>
                </Box>
                <ContainerScroll sx={{ flex: 1 }}>
                    <Box
                        sx={{
                            maxWidth: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                        }}>
                        {renderContent(activeNode, childProps)}
                    </Box>
                </ContainerScroll>
            </Box>
        </Box>
    );
}

export { AdminBody };
